/**
 * cal-range — shared typed calendar-range load for the Week / Day / Month views
 * (UIR-24, Voyage #249). `getCalendar` (GET /v1/admin/calendar) serves
 * `{ calendar: { events, blocks, staff } }` — unified entries AND availability
 * blocks (staff OOO / lunch / training / holiday / tenant closures). The views
 * render BOTH: a calendar that omits blocks presents closed time as bookable.
 * SCOPE_FORBIDDEN surfaces as an error, never as an empty grid.
 */
import { getCalendar } from "@v0y4ge/api-client";
import { t as i18nT } from "../../lib/i18n";
import { calendarDateStartIso, toParts } from "../../lib/datetime";

export interface CalRangeEvent {
  id: string; title: string; startsAt: string; endsAt: string;
  color: string; type: string; loc?: string;
}
export interface CalRangeBlock {
  id: string; title: string; startMs: number; endMs: number;
  scope: "staff" | "all-staff"; staffName?: string; blockType?: string;
}
export interface CalRange { events: CalRangeEvent[]; blocks: CalRangeBlock[]; }

export async function loadCalendarRange(from: Date, to: Date): Promise<CalRange> {
  const r = await getCalendar({ from: from.toISOString(), to: to.toISOString() }) as unknown as Record<string, any>;
  const cal = (r?.calendar ?? {}) as Record<string, any>;
  const rawEvents = (cal.events ?? r?.events ?? []) as Record<string, any>[];
  const rawBlocks = (cal.blocks ?? []) as Record<string, any>[];
  const staff = (cal.staff ?? []) as { id?: string; name?: string }[];
  const staffName = (sid?: string) => (sid ? staff.find((s) => s.id === sid)?.name : undefined);
  return {
    events: rawEvents.map((e): CalRangeEvent => ({
      id: String(e.id),
      title: String(e.title || i18nT("calendar.range.event", "Event")),
      startsAt: String(e.startsAt ?? e.start_utc ?? e.date ?? ""),
      endsAt: String(e.endsAt ?? e.end_utc ?? e.startsAt ?? ""),
      color: String(e.color || e.hue || "#5142C7"),
      type: String(e.category ?? e.entryType ?? "event"),
      loc: (e.locationName || e.subtitle) ? String(e.locationName || e.subtitle) : undefined,
    })),
    blocks: rawBlocks
      .map((b): CalRangeBlock => ({
        id: String(b.id),
        title: String(b.title || i18nT("calendar.range.unavailable", "Unavailable")),
        startMs: new Date(String(b.startsAt)).getTime(),
        endMs: new Date(String(b.endsAt)).getTime(),
        scope: b.scope === "all-staff" ? "all-staff" : "staff",
        staffName: staffName(typeof b.staffId === "string" ? b.staffId : undefined),
        blockType: typeof b.blockType === "string" ? b.blockType : undefined,
      }))
      .filter((b) => !isNaN(b.startMs) && !isNaN(b.endMs)),
  };
}

/** Clamp a block onto one day column: fractional start/end hours within
 *  [firstHour, lastHourExclusive], or null when the block misses the day. */
export function blockSpanForDay(
  b: CalRangeBlock, dayDate: Date, firstHour: number, lastHourExclusive: number,
): { startH: number; endH: number } | null {
  // Day boundary itself is resolved in the TENANT display tz, never the
  // browser's local midnight (was dayDate.setHours(0,0,0,0)) -- otherwise a
  // block that does NOT cross midnight in the tenant zone can still get
  // clamped as if it did, whenever the viewer's browser sits in a different
  // zone than the tenant.
  const dayStart = new Date(calendarDateStartIso(dayDate));
  const nextDay = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate() + 1);
  const dayEnd = new Date(calendarDateStartIso(nextDay));
  if (b.startMs >= dayEnd.getTime() || b.endMs <= dayStart.getTime()) return null;
  // Clamped fractional hour is read in the TENANT display tz (toParts), never
  // the browser's local zone (was s.getHours()) -- same defect class as the
  // event chips: an OOO block stored at 14:00 UTC / 10:00 America/New_York
  // must shade the 10:00 row, not the 14:00 row.
  const rawStart = b.startMs <= dayStart.getTime() ? firstHour : hourOf(Math.max(b.startMs, dayStart.getTime()));
  const rawEnd = b.endMs >= dayEnd.getTime() ? lastHourExclusive : hourOf(Math.min(b.endMs, dayEnd.getTime()));
  const startH = Math.max(firstHour, Math.min(rawStart, lastHourExclusive));
  const endH = Math.max(startH, Math.min(rawEnd, lastHourExclusive));
  return endH - startH <= 0 ? null : { startH, endH };
}

function hourOf(ms: number): number {
  const p = toParts(ms);
  return p ? p.h + p.mi / 60 : new Date(ms).getHours() + new Date(ms).getMinutes() / 60;
}

/** True when the failure is the calendar route's scope refusal — a real error
 *  state to render, never to be treated as an empty result. */
export function isScopeForbidden(e: unknown): boolean {
  const any = e as { code?: string; message?: string; body?: unknown } | null;
  const s = `${any?.code ?? ""} ${any?.message ?? ""} ${(() => { try { return JSON.stringify(any?.body ?? ""); } catch { return ""; } })()}`;
  return s.includes("SCOPE_FORBIDDEN");
}
